import React, { useState } from "react";
import { motion } from "framer-motion";
import { useAva } from "@/context/AvaContext";
import { api } from "@/lib/apiClient";
import { exportTextPdf } from "@/lib/pdf";
import { Volume2, VolumeX, FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const AvaOrb = () => {
  const { speaking, caption, stop, voiceEnabled, toggleVoice } = useAva();
  const [busy, setBusy] = useState(false);

  const exportAva = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ava/summary", {});
      if (!data.ok || !data.summary) { toast.message(data.reason || "No AVA discussion to summarise yet"); return; }
      const s = data.summary;
      exportTextPdf({
        title: s.title || "AVA Voice-Live Summary",
        subtitle: "AVA Voice-Live Debrief",
        meta: [`${data.turns} turns`, data.when ? new Date(data.when).toLocaleString() : ""],
        sections: [
          { heading: "Summary", body: s.summary || "" },
          { heading: "Decisions", bullets: s.decisions || [] },
          { heading: "Action Points", bullets: s.action_points || [] },
        ],
        filename: "octon-ava-debrief.pdf",
      });
      toast.success("AVA debrief PDF downloaded");
    } catch (e) {
      toast.error("Could not build the AVA debrief");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed top-20 right-4 z-40 flex flex-col items-end gap-2" data-testid="ava-orb-container">
      {caption && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
          className="glass max-w-xs rounded-2xl px-4 py-3 text-xs text-white/80" data-testid="ava-caption">
          <span className="txt-cyan font-tac text-[9px]">AVA · OCTON</span>
          <p className="mt-1 leading-relaxed">{caption}</p>
          {speaking && <button onClick={stop} className="mt-1 text-[10px] text-white/40 hover:text-white">stop</button>}
        </motion.div>
      )}
      <div className="flex items-center gap-2">
        <button onClick={toggleVoice} data-testid="ava-voice-toggle" title={voiceEnabled ? "Auto-voice ON — click to mute" : "Auto-voice OFF — click to enable"}
          className={`glass flex h-9 items-center gap-1.5 rounded-full px-3 text-[11px] font-semibold transition ${voiceEnabled ? "text-cyan-300" : "text-white/45"}`}>
          {voiceEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />} {voiceEnabled ? "Voice on" : "Voice off"}
        </button>
        <button onClick={exportAva} disabled={busy} data-testid="ava-pdf-btn" title="Download a PDF debrief of your AVA voice-live discussion"
          className="glass flex h-9 items-center gap-1.5 rounded-full px-3 text-[11px] font-semibold text-white/70 transition hover:text-white disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <FileDown size={14} />} AVA PDF
        </button>
      </div>
    </div>
  );
};
